import {
  tnvedReviewsHaveCodeChanges,
} from './tnved-review-utils'
import {
  APPLICATION_CHECK_STATUS,
  APPLICATION_CLARIFICATION_STATUS,
  APPLICATION_EVENT_TYPE,
  APPLICATION_STATUS,
  APPLICATION_TIMELINE_ENTRY_KIND,
  CHECK_BLOCK_VISUAL_STATE,
} from '../../../../config/constants'

const PREPARATION_APPLICATION_STATUSES = new Set<string>([ APPLICATION_STATUS.PREPARING, APPLICATION_STATUS.AWAITING_CLIENT ])

export function showManagerCheckItemDiscussionComposer(app: ApplicationDetail): boolean {
  return PREPARATION_APPLICATION_STATUSES.has(app.status)
}

export function allApplicationCheckItemsCompleted(app: ApplicationDetail): boolean {
  if (!app.check_items.length) {
    return false
  }
  return app.check_items.every((item) => item.status === APPLICATION_CHECK_STATUS.COMPLETED)
}

/** Первый незакрытый блок в порядке списка — «текущий»; остальные держим свёрнутыми. */
export function activeCheckItemKind(items: ApplicationCheckItem[]): string | null {
  const current = items.find((item) => item.status !== APPLICATION_CHECK_STATUS.COMPLETED)
  return current?.kind ?? null
}

/** Клиент: менеджер в работе, статус «ожидаем от вас», по блоку есть запрос. */
export function showClientCheckItemDiscussionComposer(
  app: ApplicationDetail,
  item: ApplicationCheckItem,
): boolean {
  if (item.status === APPLICATION_CHECK_STATUS.COMPLETED) {
    return false
  }
  if (!PREPARATION_APPLICATION_STATUSES.has(app.status)) {
    return false
  }
  if (!app.manager_id) {
    return false
  }
  if (app.status !== APPLICATION_STATUS.AWAITING_CLIENT) {
    return false
  }
  return checkItemBlockNeedsClientAttention({ app, item })
}

/** Полный пакет проверки (просмотр, без скрытия завершённых блоков). */
const PACKAGE_REVIEW_APPLICATION_STATUSES = new Set<string>([
  APPLICATION_STATUS.PREPARATION_COMPLETE,
  APPLICATION_STATUS.AWAITING_EXECUTOR_PROPOSAL,
  APPLICATION_STATUS.PROPOSAL_READY,
  APPLICATION_STATUS.AWAITING_EXECUTOR_CONTACT,
  APPLICATION_STATUS.AT_EXECUTOR,
])

/** Клиент видит полный пакет после подготовки, в т.ч. при закрытии заявки (отказ и т.д.). */
export function clientSeesFullPreparedPackage(app: ApplicationDetail): boolean {
  if (PACKAGE_REVIEW_APPLICATION_STATUSES.has(app.status)) {
    return true
  }
  return app.status === APPLICATION_STATUS.CLOSED && Boolean(app.preparation_completed_at)
}

export function checkBlockVisualState({ status, needsClientAttention }: ApplicationCheckBlockVisualStateArgs
): CheckBlockVisualState {
  if (status === APPLICATION_CHECK_STATUS.COMPLETED) return APPLICATION_CHECK_STATUS.COMPLETED
  if (needsClientAttention) return CHECK_BLOCK_VISUAL_STATE.NEEDS_DATA
  return CHECK_BLOCK_VISUAL_STATE.DEFAULT
}

export function checkItemNeedsTnvedClientConfirmation(item: ApplicationCheckItem): boolean {
  return item.kind === 'tnved_code'
    && tnvedReviewsHaveCodeChanges(item.tnved_reviews ?? [ ])
    && !item.tnved_changes_confirmed
}

export function checkItemBlockNeedsClientAttention({ app, item }: ApplicationClientBlockHasActionContentArgs
): boolean {
  if (checkItemNeedsTnvedClientConfirmation(item)) {
    return true
  }
  if (hasOpenClarificationForCheckKind({
    clarifications: app.clarifications,
    kind: item.kind,
    kindLabel: item.kind_label,
  })) {
    return true
  }
  const comments = item.comments ?? []
  if (!comments.length) {
    return false
  }
  const last = comments[comments.length - 1]
  return Boolean(last.posted_as_manager)
}

export function checkItemStatusLabelFromPayload({ payload, checkStatuses }: ApplicationCheckItemStatusLabelFromPayloadArgs
): string | null {
  if (!payload) return null
  const fromPayload = payload.status_label
  if (typeof fromPayload === 'string' && fromPayload.trim()) {
    return fromPayload
  }
  const status = payload.status
  if (typeof status !== 'string') return null
  const match = checkStatuses?.find((item) => item.value === status)
  return match?.label ?? status
}

export function hasOpenClarificationForCheckKind({ clarifications, kind, kindLabel }: ApplicationHasOpenClarificationForCheckKindArgs
): boolean {
  return clarificationsForCheckKind({ clarifications: clarifications, kind: kind, kindLabel: kindLabel })
    .some((c) => c.status === APPLICATION_CLARIFICATION_STATUS.OPEN)
}

/** Итоговый комментарий при закрытии блока (без дубля уточнения). */
export function blockClosingManagerNoteBody({ item, clarifications }: ApplicationManagerDraftNoteForCheckItemArgs
): string {
  if (item.status !== APPLICATION_CHECK_STATUS.COMPLETED) {
    return ''
  }
  return managerDraftNoteForCheckItem({ item, clarifications })
}

export function managerDraftNoteForCheckItem({ item, clarifications }: ApplicationManagerDraftNoteForCheckItemArgs
): string {
  if (hasOpenClarificationForCheckKind({ clarifications: clarifications, kind: item.kind, kindLabel: item.kind_label })) {
    return ''
  }
  const note = (item.manager_note || '').trim()
  if (!note) {
    return ''
  }
  const questions = clarificationsForCheckKind({ clarifications: clarifications, kind: item.kind, kindLabel: item.kind_label }).map((c) => c.question.trim())
  if (questions.includes(note)) {
    return ''
  }
  return note
}

export function clarificationsForCheckKind({ clarifications, kind, kindLabel }: ApplicationClarificationsForCheckKindArgs
): ApplicationClarification[] {
  return clarifications.filter((c) => (
    c.field_hint === kind || c.title === kindLabel
  ))
}

export function eventsForCheckKind({ events, kind }: ApplicationEventsForCheckKindArgs
): ApplicationEvent[] {
  if (!events?.length) return []
  return events.filter((event) => {
    if (event.event_type === APPLICATION_EVENT_TYPE.CHECK_ITEM_UPDATED && event.payload?.kind === kind) {
      return true
    }
    if (event.event_type === APPLICATION_EVENT_TYPE.CLARIFICATION_REQUESTED) {
      return event.payload?.field_hint === kind
    }
    if (event.event_type === APPLICATION_EVENT_TYPE.CLARIFICATION_ANSWERED) {
      return event.payload?.check_item_kind === kind
    }
    return false
  })
}

const HISTORY_DEDUP_WINDOW_MS = 2 * 60 * 1000

function isNearSameTime({ left, right }: ApplicationIsNearSameTimeArgs): boolean {
  const delta = Math.abs(new Date(left).getTime() - new Date(right).getTime())
  return delta <= HISTORY_DEDUP_WINDOW_MS
}

/** Убирает события, дублирующие записи уточнений в ленте блока */
export function filterBlockHistoryEvents({ events, clarifications }: ApplicationFilterBlockHistoryEventsArgs
): ApplicationEvent[] {
  return events.filter((event) => {
    if (event.event_type === APPLICATION_EVENT_TYPE.CLARIFICATION_REQUESTED) {
      return false
    }

    if (event.event_type !== APPLICATION_EVENT_TYPE.CHECK_ITEM_UPDATED) {
      return true
    }

    const msg = (event.message || '').trim()
    if (!msg) {
      return true
    }

    const matchingClarification = clarifications.find((c) => c.question.trim() === msg)
    if (matchingClarification && isNearSameTime({ left: event.created_at, right: matchingClarification.created_at })) {
      return false
    }

    return true
  })
}

export function buildBlockTimeline({ clarifications, events, comments = [] }: ApplicationBuildBlockTimelineArgs
): BlockTimelineEntry[] {
  const filteredEvents = filterBlockHistoryEvents({ events: events, clarifications: clarifications })
  const entries: BlockTimelineEntry[] = [
    ...clarifications.map((clarification) => ({
      kind: APPLICATION_TIMELINE_ENTRY_KIND.CLARIFICATION as const,
      at: clarification.created_at,
      clarification,
    })),
    ...comments.map((comment) => ({
      kind: APPLICATION_TIMELINE_ENTRY_KIND.COMMENT as const,
      at: comment.created_at,
      comment,
    })),
    ...filteredEvents.map((event) => ({
      kind: APPLICATION_TIMELINE_ENTRY_KIND.EVENT as const,
      at: event.created_at,
      event,
    })),
  ]

  entries.sort((left, right) => new Date(left.at).getTime() - new Date(right.at).getTime())
  return entries
}

export function orphanClarifications(app: ApplicationDetail): ApplicationClarification[] {
  return app.clarifications.filter((c) => {
    if (!c.field_hint) return true
    return !app.check_items.some((item) => item.kind === c.field_hint)
  })
}

export function clientCheckKindsToShow(app: ApplicationDetail): ApplicationCheckItem[] {
  const kinds = new Set<string>()
  app.clarifications.forEach((c) => {
    if (c.status === APPLICATION_CLARIFICATION_STATUS.OPEN && c.field_hint) {
      kinds.add(c.field_hint)
    }
  })
  return app.check_items.filter((item) => kinds.has(item.kind))
}

export function showClientCheckBlocksSection(app: ApplicationDetail): boolean {
  if (PREPARATION_APPLICATION_STATUSES.has(app.status)) {
    return app.check_items.length > 0
  }
  if (clientSeesFullPreparedPackage(app)) {
    return app.check_items.length > 0
  }
  return clientCheckKindsToShow(app).length > 0
}

/** Все блоки на этапе подготовки; позже — только требующие внимания */
export function clientCheckItemsForDisplay(app: ApplicationDetail): ApplicationCheckItem[] {
  if (PREPARATION_APPLICATION_STATUSES.has(app.status)) {
    return app.check_items
  }
  if (clientSeesFullPreparedPackage(app)) {
    return app.check_items
  }
  return clientCheckKindsToShow(app)
}

export function applicationPreparedDocumentsCount(app: ApplicationDetail): number {
  return app.check_items.reduce(
    (total, item) => total + (item.prepared_documents?.length ?? 0),
    0,
  )
}

/** ZIP доступен клиенту после завершения подготовки; менеджеру — когда есть файлы. */
export function showApplicationPreparedDocumentsZipDownload({ app, isManager }: ApplicationShowApplicationPreparedDocumentsZipDownloadArgs
): boolean {
  if (applicationPreparedDocumentsCount(app) === 0) {
    return false
  }
  if (isManager) {
    return true
  }
  if (app.preparation_completed_at) {
    return true
  }
  return clientSeesFullPreparedPackage(app)
}

export function clientBlockHasActionContent({ app, item }: ApplicationClientBlockHasActionContentArgs
): boolean {
  if (checkItemNeedsTnvedClientConfirmation(item)) {
    return true
  }
  if ((item.prepared_documents?.length ?? 0) > 0) return true
  if ((item.manager_note || '').trim()) return true
  const clarifications = clarificationsForCheckKind({ clarifications: app.clarifications, kind: item.kind, kindLabel: item.kind_label })
  if (clarifications.some((c) => c.status === APPLICATION_CLARIFICATION_STATUS.OPEN || Boolean(c.answer_text))) {
    return true
  }
  return (item.comments?.length ?? 0) > 0
}
